import { api } from './api'

/**
 * Native-shell bridge (v0.15.0, D-093). The Capacitor shells (mobile/) load this
 * SAME web bundle; the bridge object appears on window only inside a shell, so
 * every helper here is a safe no-op on the plain web. Deliberately no static
 * @capacitor/* imports — the web bundle stays byte-identical in behavior and the
 * shells access registered native plugins through the runtime bridge proxy.
 */

type CapacitorBridge = {
  getPlatform?: () => string
  isNativePlatform?: () => boolean
  isPluginAvailable?: (name: string) => boolean
  Plugins?: Record<string, {
    addListener?: (event: string, cb: (data: Record<string, unknown>) => void) => void
    [k: string]: unknown
  }>
}

const cap = (): CapacitorBridge | null =>
  (window as unknown as { Capacitor?: CapacitorBridge }).Capacitor || null

export const isNative = (): boolean => !!cap()?.isNativePlatform?.()
export const nativePlatform = (): 'ios' | 'android' | null => {
  const p = cap()?.getPlatform?.()
  return p === 'ios' || p === 'android' ? p : null
}

/** Store-billing compliance switch (D-092/D-096 web-only billing): on ANY native
 *  shell — iOS (Apple 3.1.1) AND Android (Play Payments policy) — we sell nothing
 *  and show no prices. All purchases happen on the web account. */
export const hidePurchases = (): boolean => nativePlatform() !== null

/** Register native push (APNs/FCM device token → our fa_push_subs). Fire-and-forget
 *  after login; no-op outside shells or if the PushNotifications plugin isn't in
 *  the shell build. Web push (usePush) continues to handle the browser path. */
export async function registerNativePush(): Promise<void> {
  const platform = nativePlatform()
  const c = cap()
  const push = c?.Plugins?.PushNotifications as {
    requestPermissions?: () => Promise<{ receive: string }>
    register?: () => Promise<void>
    addListener?: (ev: string, cb: (t: { value: string }) => void) => void
  } | undefined
  if (!platform || !c?.isPluginAvailable?.('PushNotifications') || !push?.register) return
  try {
    const perm = await push.requestPermissions?.()
    if (perm && perm.receive !== 'granted') return
    push.addListener?.('registration', (token) => {
      api.registerNativePushToken(platform, token.value).catch(() => {})
    })
    await push.register()
  } catch { /* push is optional — never block the app on it */ }
}

/** Biometric unlock seam for Sanctum Lock: resolves true when the shell has a
 *  biometric plugin and the user passes Face ID / fingerprint; false otherwise.
 *  Lighting this up requires adding @aparajita/capacitor-biometric-auth to the
 *  shell (mobile/README.md) — until then the passcode path is unaffected. */
export async function biometricUnlock(reason: string): Promise<boolean> {
  const c = cap()
  const bio = c?.Plugins?.BiometricAuth as {
    authenticate?: (opts: { reason: string }) => Promise<void>
    checkBiometry?: () => Promise<{ isAvailable: boolean }>
  } | undefined
  if (!isNative() || !bio?.authenticate) return false
  try {
    const check = await bio.checkBiometry?.()
    if (check && !check.isAvailable) return false
    await bio.authenticate({ reason })
    return true
  } catch { return false }
}
